import { Link } from '@remix-run/react'
import { LoadingCircle } from '~/components/common'
import { Button, Separator } from '~/components/ui'
import { getParcelLabelValues, type ParcelProperties } from '~/models'
import { cn } from '~/utils'

type MapParcelPopupProps = {
  parcels: ParcelProperties[]
  selectedParcelData: ParcelProperties | null
  selectedParcelIndex: number | null
  setSelectedParcelIndex: (index: number) => void
}

export default function MapParcelPopup({
  parcels,
  selectedParcelData,
  selectedParcelIndex,
  setSelectedParcelIndex,
}: MapParcelPopupProps) {
  return (
    <>
      {parcels.length > 1 && (
        <>
          <p className="mb-2 text-sm font-medium leading-tight">
            Multiple parcels found
          </p>

          <div className="mb-1 flex flex-nowrap gap-2 overflow-x-auto pb-1">
            {parcels.map((parcel, index) => {
              return (
                <button
                  key={parcel.ll_uuid}
                  className={cn(
                    'select-none whitespace-nowrap rounded-md px-2 py-1',
                    index === selectedParcelIndex
                      ? 'bg-primary text-white'
                      : 'bg-neutral-200'
                  )}
                  onClick={() => setSelectedParcelIndex(index)}>
                  {parcel.fields.address}
                </button>
              )
            })}
          </div>

          <Separator className="mb-2" />
        </>
      )}
      {selectedParcelData ? (
        <>
          <p className="mb-2 text-sm font-medium capitalize leading-tight">
            {[
              selectedParcelData.headline,
              selectedParcelData.fields.city,
              selectedParcelData.fields.state2,
              selectedParcelData.fields.szip5,
              selectedParcelData.fields.county
                ? `${selectedParcelData.fields.county} County`
                : null,
            ]
              .filter((v) => v)
              .join(', ')}
          </p>
          <dl className="-mr-2 max-h-96 overflow-y-auto overflow-x-hidden text-sm capitalize">
            {getParcelLabelValues(selectedParcelData).map(
              ({ key, label, value }) => {
                return (
                  <div key={key} className="mb-1 grid grid-cols-3 gap-2">
                    <dt className="col-span-1 font-medium leading-tight">
                      {label}:
                    </dt>
                    <dd className="col-span-2 capitalize">{value}</dd>
                  </div>
                )
              }
            )}
            <p className="mt-2 text-sm italic text-muted-foreground">
              This information is provided by Regrid and is not maintained by
              Resimplifi
            </p>
          </dl>
          <div className="-mb-2 -mr-3 text-right">
            <Button size="sm" variant="link">
              <Link
                to={'/admin/listings/create'}
                state={{
                  latitude: selectedParcelData.fields.lat,
                  longitude: selectedParcelData.fields.lon,
                  address: selectedParcelData.fields.address,
                  city: selectedParcelData.fields.city,
                  stateAbbr: selectedParcelData.fields.state2,
                  postalCode: selectedParcelData.fields.szip5,
                  county: selectedParcelData.fields.county,
                }}>
                Create Listing
              </Link>
            </Button>
          </div>
        </>
      ) : (
        <div className="flex h-dvh max-h-96 max-w-[400px] flex-col items-center justify-center">
          <LoadingCircle className="mb-2 h-8 w-8 text-primary" />
          Loading parcel data
        </div>
      )}
    </>
  )
}
